import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { FeatureFlagsAdminService } from '../../services/feature-flags-admin.service';
import { FeatureFlag, EvaluationContext } from '../../../models/feature-flag.model';

/**
 * Component for displaying detailed information about a feature flag
 */
@Component({
  selector: 'cbre-feature-flag-detail',
  templateUrl: './feature-flag-detail.component.html',
  styleUrls: ['./feature-flag-detail.component.scss'],
  standalone: false,
})
export class FeatureFlagDetailComponent implements OnInit {
  /**
   * ID of the feature flag to display
   */
  @Input() flagId: string;

  /**
   * Whether to show the evaluation testing panel
   */
  @Input() showEvaluation = true;

  /**
   * Event emitted when edit button is clicked
   */
  @Output() editFlag = new EventEmitter<string>();

  /**
   * Event emitted when archive operation is successful
   */
  @Output() archiveFlag = new EventEmitter<string>();

  /**
   * The feature flag data
   */
  flag: FeatureFlag;

  /**
   * Loading state
   */
  loading = true;

  /**
   * Error message
   */
  errorMessage: string;

  /**
   * Evaluation context input
   */
  evaluationContext = new FormControl(
    '{\n  "email": "user@cbre.com",\n  "userGroups": ["Catalog Admin"],\n  "userRole": "US Researcher",\n  "region": "United States Of America",\n  "lineOfBusiness": "US General"\n}'
  );

  /**
   * Evaluation result
   */
  evaluationResult: boolean | null = null;

  /**
   * Evaluation loading state
   */
  evaluating = false;

  /**
   * Evaluation error
   */
  evaluationError: string;

  /**
   * Whether the rule rendering is expanded
   */
  expandedRule = false;

  constructor(private featureFlagsService: FeatureFlagsAdminService) {}

  ngOnInit(): void {
    this.loadFeatureFlag();
  }

  /**
   * Load the feature flag data
   */
  loadFeatureFlag(): void {
    this.loading = true;
    this.errorMessage = null;

    this.featureFlagsService
      .getFeatureFlag(this.flagId)
      .pipe(
        catchError(error => {
          this.errorMessage = `Failed to load feature flag: ${error.message}`;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(flag => {
        if (flag) {
          this.flag = flag;
        }
      });
  }

  /**
   * Edit the feature flag
   */
  onEditFlag(): void {
    this.editFlag.emit(this.flagId);
  }

  /**
   * Archive the feature flag
   */
  onArchiveFlag(): void {
    if (confirm('Are you sure you want to archive this feature flag? This action cannot be undone.')) {
      this.featureFlagsService.archiveFeatureFlag(this.flagId).subscribe({
        next: () => {
          this.archiveFlag.emit(this.flagId);
        },
        error: error => {
          this.errorMessage = `Failed to archive feature flag: ${error.message}`;
        },
      });
    }
  }

  /**
   * Evaluate the feature flag with the current context
   */
  evaluateFlag(): void {
    this.evaluating = true;
    this.evaluationError = null;

    try {
      const context = JSON.parse(this.evaluationContext.value) as EvaluationContext;

      this.featureFlagsService
        .evaluateFeatureFlag(this.flagId, context)
        .pipe(
          catchError(error => {
            this.evaluationError = `Evaluation failed: ${error.message}`;
            return of(null);
          }),
          finalize(() => {
            this.evaluating = false;
          })
        )
        .subscribe(result => {
          if (result !== null) {
            this.evaluationResult = result;
          }
        });
    } catch (error) {
      this.evaluationError = `Invalid context JSON: ${error.message}`;
      this.evaluating = false;
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Toggle rule expansion
   */
  toggleRuleExpansion(): void {
    this.expandedRule = !this.expandedRule;
  }

  /**
   * Get the pretty-printed rule JSON
   */
  getPrettyRule(): string {
    return JSON.stringify(this.flag?.rule, null, 2);
  }

  /**
   * Copy the provided text to the clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(
      () => alert('Copied to clipboard!'),
      err => alert('Failed to copy: ' + err)
    );
  }
}
